import { t } from "@chuzi/shared/config";
import type { RealmId } from "@chuzi/shared/types";
import { DirectorAvatar } from "./director-avatar";

interface DefaultSceneSlateProps {
  variant: "title" | "end";
  storyTitle: string;
  directorName?: string | null;
  directorId?: string | null;
  directorAvatarUrl: string | null;
  realm: RealmId;
  /** When true the "created by" line is clickable and navigates to the director page. */
  interactive?: boolean;
  /** Called when the director credit is clicked (required when interactive is true). */
  onDirectorClick?: (directorId: string) => void;
  /** Compact mode for small containers like the coverbox card. */
  compact?: boolean;
  /** Coverbox image URL to use as the slate background. Falls back to atmospheric gradient. */
  backgroundUrl?: string | null;
  /** Fill the parent frame (viewer) instead of enforcing 16:9 aspect ratio. */
  fill?: boolean;
}

export function DefaultSceneSlate({
  variant,
  storyTitle,
  directorName,
  directorId,
  directorAvatarUrl,
  realm,
  interactive = false,
  compact = false,
  backgroundUrl,
  fill = false,
  onDirectorClick,
}: DefaultSceneSlateProps) {
  const headline = storyTitle;

  const avatarSize = compact ? 16 : 24;
  const hasDirector = !!(directorName && directorId);

  const creditLine = hasDirector ? (
    <span
      role={interactive && hasDirector ? "link" : undefined}
      tabIndex={interactive && hasDirector ? 0 : undefined}
      onClick={
        interactive && hasDirector && onDirectorClick
          ? () => onDirectorClick(directorId!)
          : undefined
      }
      onKeyDown={
        interactive && hasDirector && onDirectorClick
          ? (e: React.KeyboardEvent) => {
              if (e.key === "Enter") onDirectorClick(directorId!);
            }
          : undefined
      }
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: compact ? 4 : 6,
        cursor: interactive ? "pointer" : "default",
        opacity: 1,
        transition: "opacity 0.2s",
      }}
      onMouseEnter={(e) => {
        if (interactive) (e.currentTarget.style.opacity = "0.7");
      }}
      onMouseLeave={(e) => {
        if (interactive) (e.currentTarget.style.opacity = "1");
      }}
    >
      <span
        style={{
          width: avatarSize,
          height: avatarSize,
          borderRadius: "50%",
          overflow: "hidden",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(255,255,255,0.08)",
        }}
      >
        {directorAvatarUrl ? (
          <img
            src={directorAvatarUrl}
            alt={directorName}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <DirectorAvatar seed={directorId} realm={realm} size={avatarSize} />
        )}
      </span>
      <span>
        {t(realm, "created_by", "created by")} {directorName}
      </span>
    </span>
  ) : null;

  const isWilds = realm === "wilds";

  const atmosphericBg = isWilds
    ? "radial-gradient(ellipse at 30% 20%, rgba(60,140,60,0.25) 0%, rgba(8,20,10,0.92) 70%)"
    : "radial-gradient(ellipse at 30% 20%, rgba(80,120,200,0.25) 0%, rgba(10,14,30,0.92) 70%)";

  const background = backgroundUrl ? `url(${backgroundUrl}) center/cover no-repeat` : atmosphericBg;

  if (compact) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          background,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: creditLine ? 8 : 0,
          padding: 6,
          position: "relative",
        }}
      >
        {!backgroundUrl && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage:
                "radial-gradient(1px 1px at 20% 30%, rgba(255,255,255,0.4) 0%, transparent 100%), " +
                "radial-gradient(1px 1px at 70% 15%, rgba(255,255,255,0.3) 0%, transparent 100%), " +
                "radial-gradient(1px 1px at 45% 65%, rgba(255,255,255,0.25) 0%, transparent 100%), " +
                "radial-gradient(1px 1px at 80% 75%, rgba(255,255,255,0.35) 0%, transparent 100%), " +
                "radial-gradient(1px 1px at 10% 80%, rgba(255,255,255,0.2) 0%, transparent 100%)",
              pointerEvents: "none",
            }}
          />
        )}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "relative",
            fontSize: 11,
            fontWeight: variant === "title" ? 700 : 400,
            color: "rgba(255,255,255,0.92)",
            letterSpacing: variant === "end" ? 1.5 : 0.3,
            textTransform: variant === "end" ? "uppercase" : undefined,
            textAlign: "center",
            lineHeight: 1.3,
            wordBreak: "break-word",
            fontStyle: variant === "end" ? "italic" : undefined,
            textShadow: "0 1px 8px rgba(0,0,0,0.9)",
          }}
        >
          {headline}
        </div>

        {creditLine ? (
          <div
            style={{
              position: "relative",
              fontSize: 8,
              color: "rgba(255,255,255,0.6)",
              letterSpacing: 0.3,
              textShadow: "0 1px 4px rgba(0,0,0,0.9)",
            }}
          >
            {creditLine}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: fill ? "100%" : undefined,
        aspectRatio: fill ? undefined : "16 / 9",
        background,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: creditLine ? (variant === "title" ? 20 : 24) : 0,
        borderRadius: fill ? 0 : 10,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {!backgroundUrl && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(1px 1px at 20% 30%, rgba(255,255,255,0.4) 0%, transparent 100%), " +
              "radial-gradient(1px 1px at 70% 15%, rgba(255,255,255,0.3) 0%, transparent 100%), " +
              "radial-gradient(1px 1px at 45% 65%, rgba(255,255,255,0.25) 0%, transparent 100%), " +
              "radial-gradient(1px 1px at 80% 75%, rgba(255,255,255,0.35) 0%, transparent 100%), " +
              "radial-gradient(1px 1px at 10% 80%, rgba(255,255,255,0.2) 0%, transparent 100%)",
            pointerEvents: "none",
          }}
        />
      )}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.15) 50%, transparent 100%)",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          position: "relative",
          fontSize: variant === "title" ? 28 : 22,
          fontWeight: variant === "title" ? 700 : 400,
          color: "rgba(255,255,255,0.92)",
          letterSpacing: variant === "title" ? 0.5 : 3,
          textTransform: variant === "end" ? "uppercase" : undefined,
          textAlign: "center",
          padding: "0 24px",
          lineHeight: 1.3,
          wordBreak: "break-word",
          fontStyle: variant === "end" ? "italic" : undefined,
          textShadow: "0 2px 12px rgba(0,0,0,0.9)",
        }}
      >
        {headline}
      </div>

      {creditLine ? (
        <div
          style={{
            position: "absolute",
            left: "50%",
            bottom: 26,
            transform: "translateX(-50%)",
            fontSize: 13,
            color: "rgba(255,255,255,0.7)",
            letterSpacing: 0.5,
            textShadow: "0 1px 8px rgba(0,0,0,0.9)",
            whiteSpace: "nowrap",
          }}
        >
          {creditLine}
        </div>
      ) : null}
    </div>
  );
}
